import React, { useRef, useState, useEffect } from 'react';
import { T, font, mono, STATUS, L as L_ } from '../utils/theme';

// Mobile detection hook
export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

// Status Badge
export function StatusBadge({ status, lang = 'en' }) {
  const c = STATUS[status];
  if (!c) return null;
  return <SBadge label={lang === 'zh' ? c.zh : c.en} color={c.color} bg={c.bg} />;
}

export function SBadge({ label, color, bg }) {
  return <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color, background: bg, padding: '4px 10px', borderRadius: 5, fontFamily: font, border: `1px solid ${color}25`, whiteSpace: 'nowrap' }}>{label}</span>;
}

// Button
export function Btn({ children, variant = 'primary', style, ...p }) {
  const v = {
    primary: { background: T.accent, color: '#fff', padding: '9px 16px' },
    secondary: { background: T.surfaceAlt, color: T.text, padding: '9px 16px', border: `1px solid ${T.border}` },
    danger: { background: T.danger, color: '#fff', padding: '9px 16px' },
    ghost: { background: 'transparent', color: T.muted, padding: '7px 10px' },
    small: { background: T.accent, color: '#fff', padding: '6px 12px', fontSize: 12 },
    successSm: { background: T.success, color: '#fff', padding: '6px 12px', fontSize: 12 },
    dangerSm: { background: 'transparent', color: T.danger, padding: '6px 10px', fontSize: 11, border: `1px solid ${T.danger}40` },
    ghostSm: { background: T.surfaceAlt, color: T.muted, padding: '5px 10px', fontSize: 11, border: `1px solid ${T.border}` },
    orange: { background: T.orange, color: '#fff', padding: '5px 10px', fontSize: 11 },
  };
  return <button {...p} style={{ fontFamily: font, fontSize: 13, fontWeight: 700, border: 'none', borderRadius: 8, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s', whiteSpace: 'nowrap', ...(v[variant] || v.primary), ...style }}>{children}</button>;
}

// Input
export function Inp({ label, ...p }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, fontFamily: font, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</label>}
      <input {...p} style={{ fontFamily: font, fontSize: 14, padding: '10px 14px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, outline: 'none', ...(p.style || {}) }}
        onFocus={e => { e.target.style.borderColor = T.accent; }} onBlur={e => { e.target.style.borderColor = T.border; }} />
    </div>
  );
}

// Select
export function Sel({ label, children, ...p }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, fontFamily: font, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</label>}
      <select {...p} style={{ fontFamily: font, fontSize: 14, padding: '10px 14px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, outline: 'none', cursor: 'pointer', ...(p.style || {}) }}>{children}</select>
    </div>
  );
}

// Card
export function Card({ children, style, onClick, id, onMouseEnter, onMouseLeave, hoverBg }) {
  const baseBg = style?.background || T.surface;
  const baseBorder = style?.borderColor || T.border;
  return (
    <div id={id} onClick={onClick} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 18, transition: 'all 0.2s', ...(onClick ? { cursor: 'pointer' } : {}), ...style }}
      onMouseEnter={e => {
        if (onClick) e.currentTarget.style.borderColor = T.borderHi;
        if (hoverBg) { e.currentTarget.style.background = '#EFF6FF'; e.currentTarget.style.borderColor = '#BFDBFE'; }
        if (onMouseEnter) onMouseEnter(e);
      }}
      onMouseLeave={e => {
        if (onClick) e.currentTarget.style.borderColor = baseBorder;
        if (hoverBg) { e.currentTarget.style.background = baseBg; e.currentTarget.style.borderColor = baseBorder; }
        if (onMouseLeave) onMouseLeave(e);
      }}>
      {children}
    </div>
  );
}

// Section
export function Sect({ title, right, children }) {
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: T.text, fontFamily: font, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

// Toggle Switch
export function Toggle({ on, onToggle }) {
  return (
    <div onClick={onToggle} style={{ width: 40, height: 22, borderRadius: 11, cursor: 'pointer', transition: 'all 0.2s', background: on ? T.success : T.border, position: 'relative', flexShrink: 0 }}>
      <div style={{ width: 16, height: 16, borderRadius: 8, background: '#fff', position: 'absolute', top: 3, left: on ? 21 : 3, transition: 'left 0.2s' }} />
    </div>
  );
}

// Language Toggle
export function LangToggle({ lang, setLang }) {
  return (
    <div onClick={() => setLang(lang === 'en' ? 'zh' : 'en')} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', borderRadius: 8, overflow: 'hidden', border: `1px solid ${T.border}`, fontSize: 12, fontWeight: 700, fontFamily: font, userSelect: 'none' }}>
      <div style={{ padding: '5px 10px', background: lang === 'en' ? T.accent : T.surfaceAlt, color: lang === 'en' ? '#fff' : T.dim }}>EN</div>
      <div style={{ padding: '5px 10px', background: lang === 'zh' ? T.accent : T.surfaceAlt, color: lang === 'zh' ? '#fff' : T.dim }}>中文</div>
    </div>
  );
}

// Search helper: space-insensitive + any word order
export function matchesSearch(query, target) {
  const q = query.toLowerCase().trim();
  const t = target.toLowerCase();
  if (t.includes(q)) return true;
  if (t.replace(/\s+/g, '').includes(q.replace(/\s+/g, ''))) return true;
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length > 1 && tokens.every(tok => t.includes(tok))) return true;
  return false;
}

// Search Bar
export function SearchBar({ value, onChange, placeholder, style, sticky }) {
  const stickyStyle = sticky ? { position: 'sticky', top: 0, zIndex: 20, background: T.bg, paddingTop: 4, paddingBottom: 8 } : {};
  return (
    <div style={{ ...stickyStyle, ...style }}>
      <div style={{ position: 'relative' }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.dim} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
          <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
        </svg>
        <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          style={{ fontFamily: font, fontSize: 13, padding: '9px 14px 9px 36px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, outline: 'none', width: '100%', boxSizing: 'border-box' }} />
        {value && <span onClick={() => onChange('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: T.dim, fontSize: 14, fontWeight: 700 }}>✕</span>}
      </div>
    </div>
  );
}

// Modal
export function Modal({ open, onClose, children }) {
  // Escape closes it. The version-and-notes panel is long enough that hunting for the backdrop to
  // click is a nuisance, and every other dialog here gains the same way out.
  useEffect(() => {
    if (!open || !onClose) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 28, minWidth: 340, maxWidth: 500, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        {children}
      </div>
    </div>
  );
}

// Nav Bubble
export function NavBubble({ count }) {
  return count > 0 ? <span style={{ fontSize: 10, fontWeight: 900, background: T.danger, color: '#fff', padding: '1px 6px', borderRadius: 10, marginLeft: 6, minWidth: 18, textAlign: 'center', display: 'inline-block' }}>{count}</span> : null;
}

// View Toggle (Table / Cards)
export function ViewToggle({ mode, setMode, lang }) {
  return (
    <div style={{ display: 'flex', gap: 0, borderRadius: 6, overflow: 'hidden', border: `1px solid ${T.border}` }}>
      {['table', 'card'].map(m => (
        <div key={m} onClick={() => setMode(m)} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', background: mode === m ? T.accent : T.surfaceAlt, color: mode === m ? '#fff' : T.dim, fontFamily: font }}>
          {m === 'table' ? L_(lang, 'tableView') : L_(lang, 'cardView')}
        </div>
      ))}
    </div>
  );
}

// SVG Icons
export const Ic = ({ d, size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);
export const IcCheck = <Ic d="M20 6L9 17l-5-5" />;
export const IcPlus = <Ic d="M12 5v14M5 12h14" />;
export const IcBack = <Ic d="M19 12H5M12 19l-7-7 7-7" />;
export const IcLock = <Ic d="M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2zM7 11V7a5 5 0 0 1 10 0v4" />;
export const IcEdit = <Ic d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />;
export const IcTrash = <Ic d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />;
export const IcMsg = <Ic d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />;
export const IcPrint = <Ic d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z" />;
